import {
  ArrowLeft,
  Building2,
  ChevronDown,
  CreditCard,
  Database,
  FileInput,
  FileText,
  Gavel,
  Globe,
  GraduationCap,
  History,
  Lock,
  Network,
  PlusCircle,
  PowerOff,
  RefreshCw,
  Save,
  // UserCheck removed
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const API_BASE = apiUrl('/api/tenants');

interface TenantFormData {
  name: string; // Organization (University) Name
  corporationName: string; // Tenant (Corporation) Name
  code: string; // School Code (MEXT Code)
  ministryCode: string; // Dupe of code
  tenantId: string | null; // Corporate Number
  founderType?: string;
  establishmentDate?: string;
  domain: string;
  type: string;
  region: string;
  tenant?: {
    isContracted?: string;
  };
  status: string;
  paymentConfig: {
    provider: 'flywire' | 'gmo' | 'stripe' | 'none';
    apiKey?: string;
    contractStatus: 'active' | 'pending';
  };
  planConfig: {
    plan: 'standard' | 'enterprise';
    features: {
      multipleApplication: boolean;
      ncueeLinkage: boolean;
      jbcLinkage: boolean;
      customDomain: boolean;
    };
  };
  publicConfig: {
    displayName?: string;
    description?: string;
    contactEmail?: string;
    publicWebsite?: string;
    admissionPolicy?: string;
  };
}

interface Procedure {
  id: string;
  type: string;
  status: string;
  effectiveDate: string;
  payload: Record<string, unknown>;
}

export default function InstitutionDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sourceId = searchParams.get('id');
  const { user } = useAuth();

  // Determine target ID
  const targetId = id || sourceId;
  const isEdit = !!id;
  const isReadOnly = user?.role !== 'system_admin';

  const [loading, setLoading] = useState(false);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    corporationName: '',
    code: '',
    ministryCode: '',
    tenantId: null,
    founderType: 'private',
    establishmentDate: '',
    domain: '',
    type: 'private',
    region: 'apac',
    status: 'pending',
    paymentConfig: {
      provider: 'none',
      contractStatus: 'pending',
    },
    planConfig: {
      plan: 'standard',
      features: {
        multipleApplication: false,
        ncueeLinkage: false,
        jbcLinkage: false,
        customDomain: false,
      },
    },
    publicConfig: {},
  });

  useEffect(() => {
    if (!targetId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/${targetId}`);
        if (!res.ok) throw new Error('Failed to fetch tenant');

        const data = (await res.json()) as {
          name?: string;
          id?: string;
          tenantId?: string;
          tenant?: { name?: string };
          founderType?: string;
          establishmentDate?: string;
          domain?: string;
          storageRegion?: string;
          status?: string;
          paymentConfig?: Record<string, unknown>;
          planConfig?: Record<string, unknown>;
          publicConfig?: Record<string, unknown>;
          ncueeLinkageEnabled?: string;
          jbcLinkageEnabled?: string;
        };

        setFormData({
          name: data.name || '',
          corporationName: data.tenant?.name || '',
          code: data.id || '',
          ministryCode: data.id || '',
          tenantId: data.tenantId || null,
          founderType: data.founderType || 'private',
          establishmentDate: data.establishmentDate || '',
          domain: data.domain || '',
          tenant: data.tenant ? { isContracted: (data.tenant as { isContracted?: string }).isContracted } : undefined,
          type: 'private',
          region: data.storageRegion || 'apac',
          status: data.status || 'pending',
          paymentConfig: (data.paymentConfig as unknown as TenantFormData['paymentConfig']) || {
            provider: 'none',
            contractStatus: 'pending',
          },
          planConfig: (data.planConfig as unknown as TenantFormData['planConfig']) || {
            plan: 'standard',
            features: {
              multipleApplication: false,
              ncueeLinkage:
                data.ncueeLinkageEnabled === 'true' ||
                (data.planConfig as { features?: { ncueeLinkage?: boolean } })?.features?.ncueeLinkage === true,
              jbcLinkage:
                data.jbcLinkageEnabled === 'true' ||
                (data.planConfig as { features?: { jbcLinkage?: boolean } })?.features?.jbcLinkage === true,
              customDomain: !!data.domain,
            },
          },
          publicConfig: (data.publicConfig as unknown as TenantFormData['publicConfig']) || {},
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    const fetchProcedures = async () => {
      try {
        const res = await fetch(apiUrl(`/api/institutions/${targetId}/procedures`));
        if (res.ok) {
          const data = (await res.json()) as Procedure[];
          setProcedures(data);
        }
      } catch (e) {
        console.error('Failed to fetch procedures', e);
      }
    };

    fetchData();
    fetchProcedures();
  }, [targetId]);

  const [isProcModalOpen, setIsProcModalOpen] = useState(false);
  const [procType, setProcType] = useState<string | null>(null);
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [procPayload, setProcPayload] = useState<Record<string, unknown>>({});

  const handleAction = (action: string) => {
    setProcType(action);
    setIsProcModalOpen(true);
  };

  const getTenantStatusDisplay = () => {
    if (formData.tenantId) {
      return formData.tenant?.isContracted === 'true' ? '契約済み・運用中' : '契約済み・初期設定待ち';
    }
    return '未契約の大学・マスタデータ修正';
  };

  const handleCreateProcedure = async () => {
    try {
      setLoading(true);
      const res = await fetch(apiUrl(`/api/institutions/${targetId}/procedures`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: procType,
          effectiveDate,
          payload: procPayload,
        }),
      });

      if (res.ok) {
        setIsProcModalOpen(false);
        // Refresh procedures
        const procRes = await fetch(apiUrl(`/api/institutions/${targetId}/procedures`));
        if (procRes.ok) setProcedures(await procRes.json());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmProcedure = async (procId: string) => {
    try {
      setLoading(true);
      const res = await fetch(apiUrl(`/api/institutions/${targetId}/procedures/${procId}/confirm`), {
        method: 'PATCH',
      });
      if (res.ok) {
        const procRes = await fetch(apiUrl(`/api/institutions/${targetId}/procedures`));
        if (procRes.ok) setProcedures(await procRes.json());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyProcedure = async (procId: string) => {
    if (!confirm('マスターデータに変更を反映します。よろしいですか？')) return;
    try {
      setLoading(true);
      const res = await fetch(apiUrl(`/api/institutions/${targetId}/procedures/${procId}/apply`), {
        method: 'POST',
      });
      if (res.ok) {
        window.location.reload();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!targetId) return;

    try {
      setLoading(true);
      // Correction mode logic
      const res = await fetch(`${API_BASE}/${formData.ministryCode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          domain: formData.domain,
          storageRegion: 'apac', // Fixed to APAC
          paymentConfig: formData.paymentConfig,
          planConfig: formData.planConfig,
          publicConfig: formData.publicConfig,
        }),
      });

      if (!res.ok) throw new Error('Update failed');

      alert(t('common.success'));
      navigate('/admin/system/tenants');
    } catch (e) {
      console.error(e);
      alert(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.name) {
    return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/system/tenants"
            className="p-2 hover:bg-white rounded-full text-gray-500 transition-colors border border-transparent hover:border-gray-200 hover:shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              {isEdit ? t('tenant.editTitle') : sourceId ? t('tenant.onboardTitle') : t('tenant.newTitle')}
              {isReadOnly && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-normal border border-gray-200 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Read Only
                </span>
              )}
            </h1>
            <p className="text-gray-500 text-sm mt-1">{getTenantStatusDisplay()}</p>
          </div>
        </div>

        {/* Management Actions Dropdown - Only for Contracted or Platform Admins */}
        {isEdit && formData.tenantId && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 text-gray-700 font-medium transition-colors"
              >
                <Gavel className="w-4 h-4 text-gray-500" />
                {t('tenant.actions.menuLabel')}
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>{t('tenant.actions.menuLabel')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleAction('tenant_master')}>
                <Building2 className="w-4 h-4 mr-2" />
                {t('tenant.actions.tenantMaster')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('inst_master')}>
                <GraduationCap className="w-4 h-4 mr-2" />
                {t('tenant.actions.instMaster')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('dept_master')}>
                <Database className="w-4 h-4 mr-2" />
                {t('tenant.actions.deptMaster')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleAction('exam_master')}>
                <FileText className="w-4 h-4 mr-2" />
                {t('tenant.actions.examMaster')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('venue_master')}>
                <Globe className="w-4 h-4 mr-2" />
                {t('tenant.actions.venueMaster')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>MEXT Procedures (Draft/History)</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleAction('rename')}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('tenant.actions.rename')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('capacity')}>
                <PlusCircle className="w-4 h-4 mr-2" />
                {t('tenant.actions.capacity')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleAction('stop_recruitment')}
                className="text-amber-600 focus:text-amber-700"
              >
                <PowerOff className="w-4 h-4 mr-2" />
                {t('tenant.actions.stopRecruitment')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('abolish')} className="text-red-600 focus:text-red-700">
                <History className="w-4 h-4 mr-2" />
                {t('tenant.actions.abolish')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 space-y-8">
        {/* Section 0: Corporation Info (Tenant) */}
        {formData.tenantId && (
          <section>
            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-2">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-400" />
                {t('tenant.sectionCorporation')}
              </h3>
              <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                契約主体 (Tenant)
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1">
                <label htmlFor="corporation-name" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tenant.corporationName')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="corporation-name"
                  type="text"
                  className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border bg-white"
                  value={formData.corporationName}
                  readOnly={isReadOnly}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      corporationName: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  ※ 法人名の変更は契約情報に影響します。登記情報と一致させてください。
                </p>
              </div>
              <div className="col-span-1">
                <label htmlFor="corporate-number" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tenant.corporateNumber')}
                </label>
                <input
                  id="corporate-number"
                  type="text"
                  className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border bg-gray-50"
                  value={formData.tenantId || ''}
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">法人番号 (13桁)</p>
              </div>
            </div>
          </section>
        )}

        {/* Section 1: Institution Info */}
        <section>
          <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-2">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-gray-400" />
              {t('tenant.sectionInstitution')}
            </h3>
            <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
              運用主体 (Institution)
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2 md:col-span-1">
              <label htmlFor="university-name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('tenant.universityName')} <span className="text-red-500">*</span>
              </label>
              <input
                id="university-name"
                type="text"
                className={cn(
                  'w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border',
                  formData.ministryCode ? 'bg-gray-50' : 'bg-white',
                )}
                value={formData.name}
                // Allow edit only if not locked by MEXT code or if explicitly allowed
                readOnly={!!formData.ministryCode && isReadOnly}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.ministryCode ? 'MEXTマスタデータと連携されています。' : 'マスタデータの名称を修正できます。'}
              </p>
            </div>

            <div className="col-span-2 md:col-span-1">
              <label htmlFor="school-code" className="block text-sm font-medium text-gray-700 mb-1">
                {t('tenant.schoolCode')}
              </label>
              <input
                id="school-code"
                type="text"
                className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border bg-gray-50"
                value={formData.code}
                readOnly
              />
              <p className="text-xs text-gray-500 mt-1">学校コード (13桁)</p>
            </div>

            {/* US-A02: MEXT Fields */}
            <div className="col-span-1">
              <label htmlFor="founder-type" className="block text-sm font-medium text-gray-700 mb-1">
                設置者区分
              </label>
              <select
                id="founder-type"
                className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border bg-white"
                value={formData.founderType || 'private'}
                disabled={isReadOnly}
                onChange={(e) => setFormData({ ...formData, founderType: e.target.value })}
              >
                <option value="national">国立 (National)</option>
                <option value="public">公立 (Public)</option>
                <option value="private">私立 (Private)</option>
                <option value="vocational">専門学校</option>
              </select>
            </div>

            <div className="col-span-1">
              <label htmlFor="establishment-date" className="block text-sm font-medium text-gray-700 mb-1">
                設置認可年月日
              </label>
              <input
                id="establishment-date"
                type="date"
                className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border bg-white"
                value={formData.establishmentDate || ''}
                readOnly={isReadOnly}
                onChange={(e) => setFormData({ ...formData, establishmentDate: e.target.value })}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <label htmlFor="custom-domain" className="block text-sm font-medium text-gray-700 mb-1">
                {t('tenant.customDomain')}
              </label>
              <div className="flex max-w-lg rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  https://
                </span>
                <input
                  id="custom-domain"
                  type="text"
                  className="flex-1 w-full border-gray-300 rounded-none rounded-r-md focus:ring-indigo-500 focus:border-indigo-500 block min-w-0 sm:text-sm border px-3 py-2"
                  placeholder="apply.university.ac.jp"
                  disabled={isReadOnly}
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Contracts & Features */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <FileText className="w-5 h-5 text-gray-400" />
            {t('tenant.sectionContracts')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className={`border p-4 rounded-lg space-y-3 ${formData.planConfig.features.ncueeLinkage ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-gray-900">{t('tenant.ncuee')}</span>
                </div>
                <input
                  type="checkbox"
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                  checked={formData.planConfig.features.ncueeLinkage}
                  disabled={isReadOnly}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      planConfig: {
                        ...formData.planConfig,
                        features: {
                          ...formData.planConfig.features,
                          ncueeLinkage: e.target.checked,
                        },
                      },
                    })
                  }
                />
              </div>
              <p className="text-sm text-gray-600">{t('tenant.ncueeDesc')}</p>
            </div>

            <div
              className={`border p-4 rounded-lg space-y-3 ${formData.planConfig.features.jbcLinkage ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Network className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-gray-900">{t('tenant.jbc')}</span>
                </div>
                <input
                  type="checkbox"
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                  checked={formData.planConfig.features.jbcLinkage}
                  disabled={isReadOnly}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      planConfig: {
                        ...formData.planConfig,
                        features: {
                          ...formData.planConfig.features,
                          jbcLinkage: e.target.checked,
                        },
                      },
                    })
                  }
                />
              </div>
              <p className="text-sm text-gray-600">{t('tenant.jbcDesc')}</p>
            </div>

            <div
              className={`border p-4 rounded-lg space-y-3 ${formData.paymentConfig.provider === 'none' ? 'border-gray-200' : 'border-indigo-500 bg-indigo-50'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-gray-900">外部決済連携</span>
                </div>
                <select
                  className="block w-40 pl-3 pr-10 py-1 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  value={formData.paymentConfig.provider}
                  disabled={isReadOnly}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      paymentConfig: {
                        ...formData.paymentConfig,
                        provider: e.target.value as TenantFormData['paymentConfig']['provider'],
                      },
                    })
                  }
                >
                  <option value="none">利用なし</option>
                  <option value="flywire">Flywire</option>
                  <option value="gmo">GMO</option>
                </select>
              </div>
              <p className="text-sm text-gray-600">
                {formData.paymentConfig.provider === 'flywire' && t('tenant.flywireDesc')}
                {formData.paymentConfig.provider === 'none' && '決済機能を利用しません。'}
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Infra */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <Globe className="w-5 h-5 text-gray-400" />
            {t('tenant.sectionInfra')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="data-location" className="block text-sm font-medium text-gray-700 mb-1">
                {t('tenant.dataLocation')} <span className="text-red-500">*</span>
              </label>
              <select
                id="data-location"
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-2 border bg-gray-50"
                disabled
                value="apac"
              >
                <option value="apac">Asia Pacific (Japan / apac)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">{t('tenant.locationHelp')}</p>
            </div>
          </div>
        </section>

        {/* Section 4: Public Information */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <Globe className="w-5 h-5 text-gray-400" />
            Public Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2">
              <label htmlFor="display-name" className="block text-sm font-medium text-gray-700 mb-1">
                Display Name (Public)
              </label>
              <input
                id="display-name"
                type="text"
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-2 border"
                disabled={isReadOnly}
                value={formData.publicConfig.displayName || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    publicConfig: { ...formData.publicConfig, displayName: e.target.value },
                  })
                }
              />
            </div>
            <div className="col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-2 border"
                rows={3}
                disabled={isReadOnly}
                value={formData.publicConfig.description || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    publicConfig: { ...formData.publicConfig, description: e.target.value },
                  })
                }
              />
            </div>
          </div>
        </section>

        {/* Section 5: Procedure History (For Contracted Univs) */}
        {formData.tenantId && (
          <section className="pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-gray-400" />
              手続き・変更履歴
            </h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="bg-white text-gray-500 uppercase font-medium border-b border-gray-200 text-xs">
                  <tr>
                    <th className="px-4 py-2">種別</th>
                    <th className="px-4 py-2">適用予定日</th>
                    <th className="px-4 py-2">状態</th>
                    <th className="px-4 py-2">詳細</th>
                    <th className="px-4 py-2 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {procedures.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                        {t('tenant.institutionDetail.noHistory')}
                      </td>
                    </tr>
                  ) : (
                    procedures.map((proc) => (
                      <tr key={proc.id} className="bg-white">
                        <td className="px-4 py-3 font-medium">{t(`tenant.actions.${proc.type}`)}</td>
                        <td className="px-4 py-3 font-mono">{proc.effectiveDate}</td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'px-2 py-0.5 rounded-full text-[10px] font-bold border',
                              proc.status === 'confirmed'
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : proc.status === 'applied'
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : 'bg-gray-50 text-gray-600 border-gray-200',
                            )}
                          >
                            {proc.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[200px]">
                          {JSON.stringify(proc.payload)}
                        </td>
                        <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                          {proc.status === 'draft' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px] bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                              onClick={() => handleConfirmProcedure(proc.id)}
                            >
                              {t('tenant.institutionDetail.confirm')}
                            </Button>
                          )}
                          {proc.status === 'confirmed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px] bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                              onClick={() => handleApplyProcedure(proc.id)}
                            >
                              {t('tenant.institutionDetail.apply')}
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 text-[10px]">
                            {t('tenant.institutionDetail.view')}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-2">{t('tenant.institutionDetail.confirmedNote')}</p>
          </section>
        )}

        <div className="pt-8 border-t flex justify-end gap-3">
          <Link
            to="/admin/system/tenants"
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </Link>
          {!isReadOnly && (
            <button
              type="button"
              className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-2 shadow-sm transition-colors"
              onClick={handleSubmit}
              disabled={loading}
            >
              <Save className="w-4 h-4" />
              {loading ? t('common.loading') : isEdit ? t('common.save') : t('tenant.provision')}
            </button>
          )}
        </div>
      </div>
      {/* Procedure Modal */}
      {isProcModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h2 className="font-bold text-gray-900">
                {t('tenant.institutionDetail.procStart', { type: procType && t(`tenant.actions.${procType}`) })}
              </h2>
              <button
                type="button"
                onClick={() => setIsProcModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6 flex-1 overflow-auto">
              <div className="space-y-4">
                <div>
                  <label htmlFor="effective-date" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tenant.institutionDetail.scheduledDate')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="effective-date"
                    type="date"
                    className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                  <p className="text-[10px] text-gray-500 mt-1">{t('tenant.institutionDetail.scheduledDateHelp')}</p>
                </div>

                {procType === 'rename' && (
                  <div>
                    <label htmlFor="new-name" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('tenant.institutionDetail.newName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="new-name"
                      type="text"
                      className="w-full border-gray-300 rounded-md shadow-sm px-3 py-2 border"
                      placeholder="新しい大学・法人名を入力"
                      value={(procPayload.newName as string) || ''}
                      onChange={(e) => setProcPayload({ ...procPayload, newName: e.target.value })}
                    />
                  </div>
                )}

                {/* Other types would have their own fields here */}
              </div>

              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <h4 className="text-xs font-bold text-blue-800 mb-1 flex items-center gap-1">
                  <FileInput className="w-3 h-3" /> {t('tenant.institutionDetail.autoGen')}
                </h4>
                <p className="text-[10px] text-blue-600">
                  {t('tenant.institutionDetail.autoGenDesc')}
                  <br />
                  {t('tenant.institutionDetail.draftNote')}
                </p>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsProcModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 bg-white hover:bg-blue-50 rounded-lg flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                {t('tenant.institutionDetail.preview')}
              </button>
              <button
                type="button"
                onClick={handleCreateProcedure}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                {t('tenant.institutionDetail.saveDraft')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
